// NEW_FILE_JUSTIFIED: Certificate generation orchestrator — combines data query result with React-PDF rendering
import { pdf } from '@react-pdf/renderer';
import { FourICoverCertificate } from './FourICoverCertificate';
import type { ICertificateData } from '@/@types/certificate';

const DEFAULT_4ICOVER_BRANDING = {
  logoBase64: '',
  primaryColor: '#2A7B6F',
  accentColor: '#B8860B',
  companyName: '4iCover Global',
  planLabel: 'Medical Plan',
  contactPhone: '+357 2200 9643 / +44 203 023 0280',
  contactEmail: 'claims@4icover.com',
  website: 'www.4icover.com',
};

interface ICertificateQueryResult {
  // LOCAL_TYPE_JUSTIFIED: used only by this function; Convex query result typed at usage boundary
  plan?: { name?: string; maxAnnualBenefit?: number; currency?: string; areaOfCoverage?: string };
  policyholder?: ICertificateData['policyholder'];
  principalMember?: ICertificateData['principalMember'];
  insuredMembers?: ICertificateData['insuredMembers'];
}

export async function generateCertificatePdf(
  certificateQueryResult: ICertificateQueryResult
): Promise<Blob> {
  const planName: string = certificateQueryResult.plan?.name ?? 'Medical Plan';
  const maxBenefit: number = certificateQueryResult.plan?.maxAnnualBenefit ?? 0;
  const currency: string = certificateQueryResult.plan?.currency ?? 'EUR';
  const areaOfCoverage: string =
    certificateQueryResult.plan?.areaOfCoverage ?? 'Worldwide excl. USA';

  const data: ICertificateData = {
    branding: {
      ...DEFAULT_4ICOVER_BRANDING,
      planLabel: `Medical Plan - ${planName.replace('4iCover ', '')}`,
    },
    policyholder: certificateQueryResult.policyholder ?? {
      name: '',
      startDate: '',
      policyNumber: '',
      expiryDate: '',
    },
    principalMember: certificateQueryResult.principalMember ?? {
      name: '',
      dateOfBirth: '',
      memberId: '',
      countryOfResidence: '',
    },
    insuredMembers: certificateQueryResult.insuredMembers ?? [],
    coverSummary: {
      areaOfCoverage,
      deductible: 'Nil',
      maxAnnualBenefit: `${currency} ${maxBenefit.toLocaleString()}`,
      currency,
      keyBenefitsText: `The above mentioned persons are covered for reimbursement of medical expenses for an annual maximum sum of ${currency} ${maxBenefit.toLocaleString()} with NIL excess per medical incident for in-patient and out-patient treatment in Cyprus and abroad, including all countries of Schengen.\n\nArea of Coverage is ${areaOfCoverage}\n\nMedical Evacuation and Repatriation of mortal remains covered in full.\nCover for Covid-19 included in full.`,
      underwritingNotes: 'None',
    },
    administratorInfo: {
      name: '4i Kat Insurance Brokers',
      description:
        'This healthcare plan is administered by 4i Kat Insurance Brokers. 4i Kat Insurance Brokers is Licensed and Regulated by the Superintendent of Insurance in Cyprus under registration number 6717.',
      phone: '+357 2200 9643 / +44 203 023 0280',
      email: 'claims@4icover.com',
    },
  };

  const blob = await pdf(<FourICoverCertificate data={data} />).toBlob();
  return blob;
}
